import classNames from 'classnames';
import { useState } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';

import { IconCTA, OverlayTriggerTooltip, ShareIcon } from '@utilities';
import { narrativeStatus, reportStatus } from '@helpers';

import ModalShare from '@src/app/pages/Dashboard/components/ModalShare';

import styles from './_index.module.scss';

const Share = ({
  hasAllColleagues,
  hasException,
  hasFindColleagues,
  hasSharePublicly,
  hideOnMultiSelect,
  isHoverAction,
  isSelectedFolders,
  isSelectedItems,
  onSubmit,
  selectedRows,
  shareWithClient,
  shareWithUsers,
  type,
}) => {
  const [isActive, setIsActive] = useState(false);

  // disable share while any selected item is still processing.
  const flows = useSelector((state) => state?.websocket?.flows);
  const reports = useSelector((state) => state?.websocket?.reports);
  const normalizedType = type?.toLowerCase();
  const isNarrative = normalizedType === 'narrative';
  const isReport = normalizedType === 'report';
  const isSurvey = normalizedType === 'survey';
  const processingItems = isNarrative ? flows : reports;
  const shareableStatuses = isNarrative
    ? [narrativeStatus.COMPLETED]
    : [reportStatus.COMPLETED, reportStatus.OUT_OF_DATE];

  const isRowShareAvailable = (row) => {
    if (!isNarrative && !isReport) return true;
    const processingItem = processingItems?.find((item) => item?.id === row?.id);

    return (
      row?.isCompleted ||
      (isReport && row?.hasExpiration) ||
      shareableStatuses.includes(processingItem?.status)
    );
  };

  const isAvailable =
    isSelectedFolders || Boolean(selectedRows?.length && selectedRows.every(isRowShareAvailable));
  const capitalizedType = type?.charAt(0).toUpperCase() + type?.slice(1);
  const overlayContent =
    isAvailable || (!isNarrative && !isReport)
      ? 'Share'
      : `Share will be available after the ${capitalizedType} has finished processing`;
  const hoverText = isHoverAction ? '' : 'Share';

  const isSingleSelect = !hideOnMultiSelect || selectedRows?.length === 1;
  const isVisible = !hasException && isSingleSelect;

  if (!isVisible) return null;

  const selectedRowsIds = selectedRows?.map((selectedRow) =>
    isSelectedFolders ? selectedRow?.folderId : selectedRow?.id
  );

  return (
    <>
      <OverlayTriggerTooltip
        content={!isAvailable || isHoverAction ? overlayContent : ''}
        placement={!isHoverAction ? 'top' : undefined}
      >
        <IconCTA
          aria-disabled={!isAvailable}
          aria-label="Share"
          className={classNames(styles['share'], { [styles['is-disabled']]: !isAvailable })}
          data-testid="share-cta"
          disabled={!isAvailable}
          icon={<ShareIcon />}
          onClick={() => {
            if (isAvailable) setIsActive(true);
          }}
          text={hoverText}
        />
      </OverlayTriggerTooltip>
      {isActive && (
        <ModalShare
          hasAllColleagues={hasAllColleagues}
          hasFindColleagues={hasFindColleagues}
          hasSharePublicly={hasSharePublicly}
          ids={selectedRowsIds}
          isFolder={isSelectedFolders}
          isNarrative={isNarrative}
          isSurvey={isSurvey}
          onSubmit={() =>
            onSubmit({ refreshFolders: isSelectedFolders, refreshItems: isSelectedItems })
          }
          setShowModalShare={setIsActive}
          shareFoldersWithClients={shareWithClient}
          shareFoldersWithUsers={shareWithUsers}
          shareItemWithClients={shareWithClient}
          shareItemWithUsers={shareWithUsers}
        />
      )}
    </>
  );
};

Share.defaultProps = {
  hasSharePublicly: false,
  hideOnMultiSelect: false,
};

Share.propTypes = {
  hasAllColleagues: PropTypes.bool,
  hasException: PropTypes.bool,
  hasFindColleagues: PropTypes.bool,
  hasSharePublicly: PropTypes.bool,
  hideOnMultiSelect: PropTypes.bool,
  isHoverAction: PropTypes.bool,
  isSelectedFolders: PropTypes.bool,
  isSelectedItems: PropTypes.bool,
  onSubmit: PropTypes.func,
  selectedRows: PropTypes.array,
  shareWithClient: PropTypes.func,
  shareWithUsers: PropTypes.func,
  type: PropTypes.string,
};

export default Share;
